// Buscas da tela do Backoffice do Faturamento (#6501). Cada uma é pequena e independente;
// a regra de cada número está em src/lib/visaoGeral/backoffice.ts.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { api } from '../../src/services/api';
import { agruparAlarmes } from '../../src/lib/alarmesGeradoras';
import { normUc } from '../../src/lib/conferenciaFaturamento';
import { JANELA_ALARME_MS } from '../Geradoras/Visao3D/usinasAoVivo';
import {
  carregarGeradoras, carregarHistorico, carregarItensRateio, carregarUcs, listarRateios,
} from '../Gestao/Rateio/saldosUcsDados';
import { limparUc } from '../Gestao/Rateio/mesReferenciaRateio';
import { contarIndicadores, montarLinhasPainel, type IndicadoresPainel, type UcPainel } from '../Gestao/Rateio/saldosUcs';
import {
  acompanharTitularidades,
  type EntradaFilaRobo, type FaturaDoTitular, type GeracaoNoPeriodo, type InterrupcaoAberta,
  type PlanilhaCarregada, type TrocaTitularidade, type UsinaCadastro, type UsinaSemReportar,
} from '../../src/lib/visaoGeral/backoffice';

const PAGINA = 1000;

async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

/** Busca simples: `null` enquanto carrega, `erro` se falhar. */
function useBusca<T>(buscar: () => Promise<T>, deps: unknown[], rotulo: string): { dados: T | null; erro: boolean } {
  const [estado, setEstado] = useState<{ dados: T | null; erro: boolean }>({ dados: null, erro: false });
  useEffect(() => {
    let vivo = true;
    setEstado({ dados: null, erro: false });
    buscar()
      .then(dados => { if (vivo) setEstado({ dados, erro: false }); })
      .catch(e => { console.error(`${rotulo}:`, e); if (vivo) setEstado({ dados: null, erro: true }); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return estado;
}

/** Planilhas do Rateio Antigo (poucas linhas: uma por carga). */
export const usePlanilhasRateio = () => useBusca<PlanilhaCarregada[]>(async () => {
  const { data, error } = await supabase.from('rateios_credito').select('id, mes_referencia, created_at, usuario_nome');
  if (error) throw error;
  return (data || []) as PlanilhaCarregada[];
}, [], 'Planilhas do rateio');

/** Fila do robô da Equatorial no mês ("OUT/2026"). */
export const useFilaDoRobo = (mes: string) => useBusca<EntradaFilaRobo[]>(() =>
  paginar<EntradaFilaRobo>(() => supabase.from('fatura_download_queue')
    .select('id, uc_codigo, mes_referencia, status, error_details, updated_at')
    .eq('mes_referencia', mes)
    .order('id')), [mes], 'Fila do robô');

/**
 * UCs (número normalizado) que receberam fatura pelo e-mail da Vigor em cada mês
 * ('AAAA-MM', o formato de `extracoes_faturas`).
 */
export const useUcsComFaturaPorEmail = (mesesIso: string[]) => {
  const chave = mesesIso.join(',');
  return useBusca<Map<string, Set<string>>>(async () => {
    const linhas = await paginar<{ uc_codigo_norm: string | null; mes_referencia: string | null }>(() =>
      supabase.from('extracoes_faturas')
        .select('id, uc_codigo_norm, mes_referencia')
        .eq('origem', 'robo-assinatura')
        .in('mes_referencia', chave.split(','))
        .order('id'));
    const porMes = new Map<string, Set<string>>();
    for (const l of linhas) {
      const uc = normUc(l.uc_codigo_norm);
      if (!uc || !l.mes_referencia) continue;
      if (!porMes.has(l.mes_referencia)) porMes.set(l.mes_referencia, new Set());
      porMes.get(l.mes_referencia)!.add(uc);
    }
    return porMes;
  }, [chave], 'Faturas pelo e-mail');
};

/** Trocas de titularidade e as faturas que chegaram depois da assinatura. */
export const useTitularidades = () => useBusca(async () => {
  const { data: logs, error } = await supabase.from('termo_troca_titularidade_logs')
    .select('id, negociacao_id, uc_id, uc_numero, cliente_nome, cpf_cnpj_cliente, assinado_em, created_at')
    .order('created_at');
  if (error) throw error;
  const trocas = (logs || []) as TrocaTitularidade[];
  if (trocas.length === 0) return acompanharTitularidades([], [], new Map());

  const idsNeg = [...new Set(trocas.map(t => t.negociacao_id))];
  const idsUc = [...new Set(trocas.map(t => t.uc_id).filter((v): v is number => v != null))];
  const [negs, ucs] = await Promise.all([
    supabase.from('negociacoes').select('id, status, titulo').in('id', idsNeg),
    idsUc.length ? supabase.from('unidades_consumidoras').select('id, numero_uc, outro_numero_uc').in('id', idsUc) : Promise.resolve({ data: [], error: null }),
  ]);
  if (negs.error) throw negs.error;
  if (ucs.error) throw ucs.error;
  const numerosDaUc = new Map<number, string[]>(((ucs.data || []) as any[]).map(u => [u.id, [u.numero_uc, u.outro_numero_uc].filter(Boolean)]));

  const numeros = new Set<string>();
  for (const t of trocas) {
    for (const n of [t.uc_numero, ...(t.uc_id != null ? numerosDaUc.get(t.uc_id) ?? [] : [])]) {
      const c = normUc(n);
      if (c) numeros.add(c);
    }
  }
  const desde = trocas.filter(t => t.assinado_em).map(t => t.assinado_em!).sort()[0];
  const faturas = desde && numeros.size
    ? await paginar<FaturaDoTitular>(() => supabase.from('extracoes_faturas')
        .select('id, uc_codigo_norm, cpf_cnpj, mes_referencia, created_at')
        .in('uc_codigo_norm', [...numeros])
        .gte('created_at', desde)
        .order('id'))
    : [];
  const negociacoes = new Map(((negs.data || []) as any[]).map(n => [Number(n.id), { status: n.status ?? null, titulo: n.titulo ?? null }]));
  return acompanharTitularidades(trocas, faturas, negociacoes, numerosDaUc);
}, [], 'Titularidades');

/** Cadastro das usinas, as paradas (mesma função do aviso) e os alarmes abertos nas últimas 72 h. */
export const useUsinasBase = () => useBusca(async () => {
  const [unidades, semReportar, alarmes] = await Promise.all([
    api.geradoras.getUnidades(),
    api.geradoras.getUsinasParadas(),
    api.geradoras.getAlarmes().catch(() => []),
  ]);
  const agora = Date.now();
  const alarmesPorSite = new Map<string, number>();
  for (const e of agruparAlarmes(alarmes)) {
    if (e.encerrado || Date.parse(e.inicio) < agora - JANELA_ALARME_MS) continue;
    const site = e.representante.site_id_api;
    alarmesPorSite.set(site, (alarmesPorSite.get(site) ?? 0) + 1);
  }
  return {
    unidades: unidades as unknown as UsinaCadastro[],
    semReportar: semReportar as UsinaSemReportar[],
    alarmesPorSite,
  };
}, [], 'Usinas agora');

/**
 * Interrupções dos últimos 3 dias (a detecção leva alguns segundos; por isso fica
 * separada e chega depois do resto).
 */
export const useInterrupcoesAbertas = () => useBusca<InterrupcaoAberta[]>(async () => {
  const fim = new Date();
  const lista = await api.geradoras.getInterrupcoes(new Date(fim.getTime() - 3 * 86_400_000), fim);
  return lista.filter(i => i.em_aberto);
}, [], 'Interrupções das usinas');

/** Geração por usina de hoje (até agora) e de ontem (dia inteiro). */
export const useGeracaoDoDia = (sites: string[] | null) => {
  const chave = sites ? sites.join(',') : null;
  return useBusca<{ hoje: GeracaoNoPeriodo[]; ontem: GeracaoNoPeriodo[] } | null>(async () => {
    if (!chave) return null;
    const lista = chave.split(',');
    const agora = new Date();
    const inicioHoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
    const inicioOntem = new Date(inicioHoje.getTime() - 86_400_000);
    const [hoje, ontem] = await Promise.all([
      api.geradoras.getResumoGeracao(lista, inicioHoje, agora),
      api.geradoras.getResumoGeracao(lista, inicioOntem, inicioHoje),
    ]);
    return { hoje, ontem };
  }, [chave], 'Geração do dia');
};

/**
 * Indicadores do painel Saldos das UCs, no rateio mais recente (o mesmo que o painel
 * abre). Mesma busca e mesma conta do painel.
 */
export const useSaldosDasUcs = () => useBusca<{ mes: string; indicadores: IndicadoresPainel } | null>(async () => {
  const [rateios, ucs, geradoras] = await Promise.all([listarRateios(), carregarUcs(), carregarGeradoras()]);
  const atual = rateios[0];
  if (!atual) return null;
  const anterior = rateios[1];
  const [itens, itensAnteriores] = await Promise.all([
    carregarItensRateio(atual.id),
    anterior ? carregarItensRateio(anterior.id) : Promise.resolve(null),
  ]);
  const porChave = new Map<string, UcPainel>();
  for (const uc of ucs) for (const c of [limparUc(uc.numero_uc), limparUc(uc.outro_numero_uc)]) if (c) porChave.set(c, uc);
  const doRateio = new Map<number, UcPainel>();
  for (const item of itens) {
    const uc = porChave.get(limparUc(item.uc)) || porChave.get(limparUc(item.outro_numero_uc));
    if (uc) doRateio.set(uc.id, uc);
  }
  const historico = await carregarHistorico([...doRateio.values()]);
  const linhas = montarLinhasPainel({ itens, itensAnteriores, ucs, historicoPorUc: historico, ugs: geradoras.ugs, mesRateio: atual.mes_referencia });
  return { mes: atual.mes_referencia, indicadores: contarIndicadores(linhas) };
}, [], 'Saldos das UCs');
